import React, { useState } from "react";
import { useNavigate } from "react-router";
import styled, { keyframes } from "styled-components";
import { useAppSelector } from "../app/hooks";
import { profileState } from "../app/reducers/profile-slice";
import { VERSION_NUMBER } from "../types/constant";
import { Pathname } from "../types/pathname";
import { HeaderMenu } from "./menu/HearderMenu";
import { ProfileMenu } from "./menu/ProfileMenu";
import { includesPermission, Permission } from "../utils/permission/permission";
import { isAtLeastAdmin } from "../types/user/user-type";
import { isSuperuser } from "../utils/user/check-user-type";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InventoryIcon from '@mui/icons-material/Inventory';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RouteIcon from '@mui/icons-material/Route';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const menuMap = {
  "등록·배정": Pathname.ScheduleForm,
  "조회": Pathname.ScheduleCheck,
};

const Header = () => {
  const history = useNavigate();
  const { profile } = useAppSelector(profileState);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const moveToPage = (event: React.MouseEvent<HTMLButtonElement>) => {
    history(event.currentTarget.name);
    setIsMobileMenuOpen(false);
  };

  const handleClickHeaderTitle = () => {
    history(Pathname.Dashboard);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <HeaderContainer>
      <HeaderTitleContainer onClick={handleClickHeaderTitle}>
        <LeftTitleContent>옹고잉</LeftTitleContent>
        <RightTitleContent>실시간 배송조회</RightTitleContent>
        <VersionLabel>{VERSION_NUMBER}</VersionLabel>
      </HeaderTitleContainer>

      <MobileMenuButton onClick={toggleMobileMenu}>
        {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
      </MobileMenuButton>

      <ButtonContainer $isOpen={isMobileMenuOpen}>
        <TabButton name={Pathname.Dashboard} onClick={moveToPage}>
          <TabButtonContent>
            <LocalShippingIcon />
            배송
          </TabButtonContent>
        </TabButton>
        <MenuDivider />
        <HeaderMenu menuMap={menuMap} label="스케줄" icon={<CalendarMonthIcon />} />
        <MenuDivider />
        <TabButton name={Pathname.DeliveryCreate} onClick={moveToPage}>
          <TabButtonContent>
            <InventoryIcon />
            물류
          </TabButtonContent>
        </TabButton>
        <MenuDivider />
        <TabButton name={Pathname.User} onClick={moveToPage}>
          <TabButtonContent>
            <GroupIcon />
            사용자
          </TabButtonContent>
        </TabButton>
        {profile?.company.name === "SUPERUSER" && (
          <>
            <MenuDivider />
            <TabButton name={Pathname.Company} onClick={moveToPage}>
              <TabButtonContent>
                <BusinessIcon />
                회사
              </TabButtonContent>
            </TabButton>
          </>
        )}
        <MenuDivider />
        <TabButton name={Pathname.Location} onClick={moveToPage}>
          <TabButtonContent>
            <LocationOnIcon />
            장소
          </TabButtonContent>
        </TabButton>
        {(isSuperuser(profile) || includesPermission(profile?.company.roles ?? [], Permission.ACCESS_ROUTING_SERVICE)) && (
          <>
            <MenuDivider />
            <TabButton name={Pathname.RoutingService} onClick={moveToPage}>
              <TabButtonContent>
                <RouteIcon />
                동선 최적화
              </TabButtonContent>
            </TabButton>
          </>
        )}
        {(isSuperuser(profile) || includesPermission(profile?.company.roles ?? [], Permission.ACCESS_SLA_REPORT)) && (
          <>
            <MenuDivider />
            <TabButton name={Pathname.Report} onClick={moveToPage}>
              <TabButtonContent>
                <AssessmentIcon />
                SLA 리포트
              </TabButtonContent>
            </TabButton>
          </>
        )}
        {isAtLeastAdmin(profile?.userType) && (
          <>
            <MenuDivider />
            <TabButton name={Pathname.Setting} onClick={moveToPage}>
              <TabButtonContent>
                <SettingsIcon />
                앱 설정
              </TabButtonContent>
            </TabButton>
          </>
        )}
        <ProfileMenu />
      </ButtonContainer>
    </HeaderContainer>
  );
};

export default Header;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  height: 56px;
  width: 100vw;
  background: #1c2026;
  overflow-y: visible;
  overflow-x: visible;
  position: relative;
`;

const HeaderTitleContainer = styled.div`
  display: flex;
  margin: 0 0 0 24px;
  cursor: pointer;
  align-items: end;
  gap: 4px;
`;

const LeftTitleContent = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #fcca00;

  @media (max-width: 715px) {
    display: none;
  }
`;

const RightTitleContent = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;

  @media (max-width: 830px) {
    display: none;
  }
`;

const ButtonContainer = styled.div<{ $isOpen: boolean }>`
  display: flex;
  margin-left: auto;
  position: relative;
  align-items: center;

  @media (max-width: 1100px) {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    background: #1c2026;
    flex-direction: column;
    align-items: stretch;
    transform: translateY(${props => props.$isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease-in-out;
    z-index: 1000;
    padding: 16px 0;
    height: calc(100vh - 56px);
    overflow-y: auto;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  margin-left: auto;
  margin-right: 16px;
  position: relative;
  z-index: 1001;

  @media (max-width: 1100px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    font-size: 24px;
  }
`;

const TabButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  svg {
    font-size: 16px;
  }
`;

const TabButton = styled.button`
  font-weight: 600;
  height: 56px;
  background-color: transparent;
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0.02857em;
  border: none;
  color: #ffffff;
  cursor: pointer;
  border-radius: 4px;
  padding: 0 10px;
  white-space: nowrap;

  @media (max-width: 1100px) {
    width: 100%;
    text-align: left;
    padding: 12px 24px;
    height: auto;
    font-size: 14px;
  }

  &:hover {
    color: #fcca00;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

const VersionLabel = styled.div`
  font-weight: 400;
  font-size: 10px;
  line-height: 18px;
  color: rgba(255, 255, 255, 0.5);
  margin-left: 4px;

  @media (max-width: 870px) {
    display: none;
  }
`;

const MenuDivider = styled.div`
  width: 1px;
  height: 20px;
  background: linear-gradient(to bottom, rgba(252, 202, 0, 0), rgba(252, 202, 0, 0.2), rgba(252, 202, 0, 0));
  margin: 0 2px;
  align-self: center;

  @media (max-width: 1100px) {
    display: none;
  }
`;